/**
 * A site's Nodes tab (JSS#1158): the joined box list with its node actions
 * (cordon, drain, reboot, taints, Talos diagnostics) and, for Engineer+, the
 * node pools. This was the Nodes and Node pools sub-tabs of the old Cluster
 * tab; the action mutations and dialogs moved with it unchanged.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Role, canActAs } from '@jeket-cloud/types/auth';
import type { ClusterNode, Machine, NodeTaint } from '@/types';
import { clusterApi } from '@/api/client';
import { useClusterNodes } from './useClusterData';
import { RoleGate } from '@/components/auth/RoleGate';
import { useUserRoles } from '@/contexts/AuthContext';
import NodesPanel from './NodesPanel';
import NodePoolsPanel from './NodePoolsPanel';
import NodeActionDialog from './NodeActionDialog';
import DrainConfirmDialog from './DrainConfirmDialog';
import TaintEditorDialog from './TaintEditorDialog';
import TalosNodeDrawer from './TalosNodeDrawer';

interface Props {
  siteId: string;
  machines: Machine[];
}

export default function NodesTab({ siteId, machines }: Props) {
  const [actionTarget, setActionTarget] = useState<{ node: ClusterNode; action: string } | null>(null);
  const [drainTarget, setDrainTarget] = useState<ClusterNode | null>(null);
  const [taintTarget, setTaintTarget] = useState<ClusterNode | null>(null);
  const [diagnosticsTarget, setDiagnosticsTarget] = useState<ClusterNode | null>(null);

  const queryClient = useQueryClient();
  const userRoles = useUserRoles();
  const { data: nodes, isLoading: nodesLoading, isError: nodesError } = useClusterNodes(siteId);

  // Engineer-tier Talos diagnostics gate (#248). The BFF is the security
  // boundary; this just hides the Diagnostics menu item from non-Engineers.
  const canSeeDiagnostics = canActAs(userRoles, Role.Engineer);
  // Admin-tier taint editor gate (#256) — same belt-and-braces pattern.
  const canEditTaints = canActAs(userRoles, Role.Admin);

  const nodeActionMutation = useMutation({
    mutationFn: async ({ node, action }: { node: ClusterNode; action: string }) => {
      // Use management IP (SideroLink/WG) for Talos actions, K8s InternalIP for cordon
      const k8sIp = node.addresses.find(a => a.type === 'InternalIP')?.address ?? node.name;
      const talosIp = node.managementIp ?? k8sIp;

      if (action === 'cordon') {
        return clusterApi.cordonNode(siteId, k8sIp, true);
      }
      if (action === 'uncordon') {
        return clusterApi.cordonNode(siteId, k8sIp, false);
      }
      return clusterApi.nodeAction(siteId, talosIp, action);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cluster-nodes', siteId] });
      queryClient.invalidateQueries({ queryKey: ['cluster-health-live', siteId] });
      setActionTarget(null);
    },
    onError: (err) => {
      window.alert(`Action failed: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  // Drain (#256) — separate mutation since drain has its own preview dialog.
  const drainMutation = useMutation({
    mutationFn: async (node: ClusterNode) => {
      const k8sIp = node.addresses.find(a => a.type === 'InternalIP')?.address ?? node.name;
      return clusterApi.drainNode(siteId, k8sIp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cluster-nodes', siteId] });
      queryClient.invalidateQueries({ queryKey: ['cluster-health-live', siteId] });
      setDrainTarget(null);
    },
    onError: (err) => {
      window.alert(`Drain failed: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  // Taint editor (#256) — admin-tier PATCH against the node taints array.
  const taintMutation = useMutation({
    mutationFn: async ({ node, taints }: { node: ClusterNode; taints: NodeTaint[] }) => {
      const k8sIp = node.addresses.find(a => a.type === 'InternalIP')?.address ?? node.name;
      return clusterApi.patchNodeTaints(siteId, k8sIp, taints);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cluster-nodes', siteId] });
      setTaintTarget(null);
    },
    onError: (err) => {
      window.alert(`Failed to update taints: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  function handleNodeAction(node: ClusterNode, action: string) {
    // Drain has its own safety-prompt dialog (#256) with a drain-preview.
    if (action === 'drain') {
      setDrainTarget(node);
      return;
    }
    setActionTarget({ node, action });
  }

  function confirmAction() {
    if (actionTarget) {
      nodeActionMutation.mutate(actionTarget);
    }
  }

  return (
    <div className="space-y-4">
      {/* The joined box list (#804): machines + K8s nodes on one row, with
          guided recovery (Reset to maintenance / Reinstall) owned by the panel. */}
      <NodesPanel
        siteId={siteId}
        nodes={nodes}
        isLoading={nodesLoading}
        nodesError={nodesError}
        onNodeAction={handleNodeAction}
        machines={machines}
        onNodeDiagnostics={canSeeDiagnostics ? setDiagnosticsTarget : undefined}
        onNodeEditTaints={canEditTaints ? setTaintTarget : undefined}
      />

      {/* Node pools (JSS#1104): Engineers see the layout and stages, an Admin
          edits them (the panel gates its own controls). Hidden, not
          disabled, below Engineer — as the Node pools sub-tab was. */}
      <RoleGate role={Role.Engineer}>
        <section data-testid="site-node-pools">
          <NodePoolsPanel siteId={siteId} />
        </section>
      </RoleGate>

      {/* Action confirmation dialog */}
      {actionTarget && (
        <NodeActionDialog
          node={actionTarget.node}
          action={actionTarget.action}
          onConfirm={confirmAction}
          onCancel={() => setActionTarget(null)}
          isLoading={nodeActionMutation.isPending}
        />
      )}

      {/* Drain preview + safety prompt (#256) */}
      {drainTarget && (
        <DrainConfirmDialog
          siteId={siteId}
          node={drainTarget}
          onConfirm={() => drainMutation.mutate(drainTarget)}
          onCancel={() => setDrainTarget(null)}
          isLoading={drainMutation.isPending}
        />
      )}

      {/* Admin-tier taint editor (#256) */}
      {taintTarget && canEditTaints && (
        <TaintEditorDialog
          node={taintTarget}
          onSave={(taints) => taintMutation.mutate({ node: taintTarget, taints })}
          onCancel={() => setTaintTarget(null)}
          isLoading={taintMutation.isPending}
        />
      )}

      {/* Engineer-tier Talos diagnostics drawer (#248) */}
      {diagnosticsTarget && canSeeDiagnostics && (
        <TalosNodeDrawer
          siteId={siteId}
          nodeIp={
            diagnosticsTarget.managementIp ??
            diagnosticsTarget.addresses.find((a) => a.type === 'InternalIP')?.address ??
            diagnosticsTarget.name
          }
          nodeName={diagnosticsTarget.name}
          onClose={() => setDiagnosticsTarget(null)}
        />
      )}
    </div>
  );
}
